import { useState, memo } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

function SortableFotoCell({ id, disabled, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition: disabled ? undefined : transition,
    opacity: isDragging ? 0.45 : 1,
    zIndex: isDragging ? 2 : undefined,
  };

  return (
    <div ref={setNodeRef} style={style} className="relative [contain:layout]">
      {children({ dragAttributes: attributes, dragListeners: listeners, isDragging })}
    </div>
  );
}

const DragOverlayThumb = memo(function DragOverlayThumb({ src, index }) {
  return (
    <div className="relative w-[88px] h-[88px] rounded-lg overflow-hidden shadow-2xl border-2 border-blue-500 bg-white rotate-1">
      <img src={src} alt="" className="w-full h-full object-cover pointer-events-none" draggable={false} />
      <div className="absolute top-1 left-1 bg-black/70 text-white text-[10px] rounded px-1 font-bold">
        {index + 1}
      </div>
    </div>
  );
});

/**
 * Grid de fotos con reordenación dnd-kit (estándar industria).
 * modoReordenar=false → solo visualización/edición normal.
 */
export default function FotoSortableGrid({
  fotos,
  setFotos,
  modoReordenar,
  renderItem,
  srcForFoto,
  className = "grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3 relative",
}) {
  const [activeId, setActiveId] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const ids = fotos.map((f) => f.id);
  const activeIndex = activeId != null ? fotos.findIndex((f) => f.id === activeId) : -1;
  const activeFoto = activeIndex >= 0 ? fotos[activeIndex] : null;

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over || active.id === over.id) return;
    setFotos((prev) => {
      const oldIndex = prev.findIndex((f) => f.id === active.id);
      const newIndex = prev.findIndex((f) => f.id === over.id);
      if (oldIndex < 0 || newIndex < 0) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={(e) => setActiveId(e.active.id)}
      onDragCancel={() => setActiveId(null)}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={ids} strategy={rectSortingStrategy}>
        <div className={className + (modoReordenar ? " foto-grid-reordenando" : "")}>
          {fotos.map((foto, i) => (
            <SortableFotoCell key={foto.id} id={foto.id} disabled={!modoReordenar}>
              {({ dragAttributes, dragListeners, isDragging }) =>
                renderItem(foto, i, {
                  compact: modoReordenar,
                  dragAttributes,
                  dragListeners,
                  isDragging,
                })
              }
            </SortableFotoCell>
          ))}
        </div>
      </SortableContext>
      <DragOverlay dropAnimation={{ duration: 180, easing: "ease" }}>
        {activeFoto && modoReordenar ? (
          <DragOverlayThumb src={srcForFoto(activeFoto)} index={activeIndex} />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
